<script>
import PageHeader from '@/components/ux/pageHeader/page-header.vue';
import bem from '@/utils/bem';
import { mapActions, mapGetters } from 'vuex';
import { SaveOutlined } from '@ant-design/icons-vue';

const b = bem('permissions-page');

/**
 * All permission keys and their human-readable labels, separated by type.
 * Maintained inline so this page is fully self-contained (no columns.js dependency).
 * When the backend adds a new permission, add it to the relevant array below.
 */
const PAGE_PERMISSIONS = [
  { key: 'UserAdminPage', label: 'User Admin' },
  { key: 'DigitizePage', label: 'Digitize' },
  { key: 'EnhancePage', label: 'Enhance' },
  { key: 'CreatePage', label: 'Create' },
  { key: 'CollectionsPage', label: 'Collections' },
  { key: 'GiftCardPage', label: 'Gift Card' },
  { key: 'DeliveriesPage', label: 'Deliveries' },
  { key: 'BlogPage', label: 'Blog' },
  { key: 'ZipCodesPage', label: 'Zip Codes' },
  { key: 'MarketingPage', label: 'Marketing' },
  { key: 'PromotionsPage', label: 'Promotions' },
  { key: 'SettingsPage', label: 'Settings' },
  { key: 'RushPage', label: 'Rush' },
  { key: 'HelpCenterPage', label: 'Help Center' },
  { key: 'PermissionsPage', label: 'Permissions' },
  { key: 'ProjectsPage', label: 'Projects' },
  { key: 'TasksPage', label: 'Tasks' },
  { key: 'SubTasksPage', label: 'Sub Tasks' },
  { key: 'ClientsPage', label: 'Clients' },
  { key: 'TicketsPage', label: 'Tickets' },
  { key: 'ReportsPage', label: 'Reports' },
  { key: 'AdminPage', label: 'Admin' },
  { key: 'OrdersPage', label: 'Orders' },
  { key: 'CartPage', label: 'Cart' },
  { key: 'EvaluationPage', label: 'Leads' },
  { key: 'SubscriptionsPage', label: 'Subscriptions' },
  { key: 'SmsPage', label: 'SMS' },
];

const ACTION_PERMISSIONS = [
  { key: 'refunds', label: 'Refunds' },
  { key: 'order-items:put', label: 'Change Order Items' },
  { key: 'user-subscription:post', label: 'Add Subscription to Account' },
  { key: 'user-subscription:put', label: 'Change Subscription in Account' },
  { key: 'tasks:put', label: 'Tasks Change' },
  { key: 'subtasks:put', label: 'Subtasks Change' },
  { key: 'contact:put', label: 'Contact Change' },
  { key: 'price', label: 'Change Price' },
  { key: 'payment-methods:get', label: 'See Payment Info' },
  { key: 'additionalDiscount', label: 'Discounts' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'history', label: 'View History' },
  { key: 'order:put', label: 'Add to Order' },
  { key: 'order:delete', label: 'Delete Orders' },
  { key: 'deleteTask', label: 'Delete Task' },
  { key: 'deleteSubtask', label: 'Delete Subtask' },
  { key: 'fresh-sales:put', label: 'Change Placed By' },
];

export default {
  components: { PageHeader, SaveOutlined },

  data() {
    return {
      title: 'Permissions',
      pagePermissions: PAGE_PERMISSIONS,
      actionPermissions: ACTION_PERMISSIONS,
      /**
       * Local editable copy of each role's permissions, keyed by role id.
       * Structure: { [roleId]: { permissions: [...], pagePermissions: [...] } }
       */
      localState: {},
      /** Tracks which role cards have unsaved modifications */
      dirtyRoles: {},
      /** Tracks which role cards are currently saving */
      savingRoles: {},
    };
  },

  computed: {
    ...mapGetters({
      roles: 'permissions/sortedPermissions',
    }),
  },

  watch: {
    /**
     * Whenever the Vuex roles array updates (initial fetch or post-save refresh),
     * rebuild the local editable state so each card reflects the latest server data.
     */
    roles: {
      handler(newRoles) {
        if (!newRoles || !newRoles.length) return;
        this.syncLocalState(newRoles);
      },
      immediate: true,
    },
  },

  mounted() {
    this.getPermissionsAction();
  },

  methods: {
    ...mapActions({
      getPermissionsAction: 'permissions/getPermissions',
      updatePermissionAction: 'permissions/updatePermissions',
    }),

    b(el, mods) {
      return b(el, mods);
    },

    /** Copy server role data into local editable state, clearing dirty flags */
    syncLocalState(roles) {
      const state = {};
      roles.forEach((role) => {
        state[role.id] = {
          permissions: [...(role.permissions || [])],
          pagePermissions: [...(role.pagePermissions || [])],
        };
      });
      this.localState = state;
      this.dirtyRoles = {};
    },

    /** Count how many permissions of a given type are enabled for a role */
    enabledCount(roleId, type) {
      const local = this.localState[roleId];
      if (!local) return 0;
      const arr = type === 'page' ? local.pagePermissions : local.permissions;
      const list = type === 'page' ? this.pagePermissions : this.actionPermissions;
      return list.filter((p) => arr.includes(p.key)).length;
    },

    /** Check whether a specific permission is enabled in the local state */
    isEnabled(roleId, permKey, type) {
      const local = this.localState[roleId];
      if (!local) return false;
      const arr = type === 'page' ? local.pagePermissions : local.permissions;
      return arr.includes(permKey);
    },

    /** Toggle a single permission in the local state and mark the card dirty */
    togglePermission(roleId, permKey, type) {
      const local = this.localState[roleId];
      if (!local) return;

      const field = type === 'page' ? 'pagePermissions' : 'permissions';
      const arr = local[field];

      if (arr.includes(permKey)) {
        local[field] = arr.filter((k) => k !== permKey);
      } else {
        local[field] = [...arr, permKey];
      }

      this.dirtyRoles = { ...this.dirtyRoles, [roleId]: true };
    },

    /** Persist the local state for a single role back to the server */
    async saveRole(roleId) {
      const local = this.localState[roleId];
      if (!local) return;

      this.savingRoles = { ...this.savingRoles, [roleId]: true };

      await this.updatePermissionAction({
        id: roleId,
        permissions: local.permissions,
        pagePermissions: local.pagePermissions,
      });

      /**
       * updatePermissionAction dispatches getPermissions internally,
       * which triggers the watcher to rebuild localState and clear dirty flags.
       * We just need to clear the saving indicator.
       */
      this.savingRoles = { ...this.savingRoles, [roleId]: false };
    },
  },
};
</script>

<template>
  <PageHeader :title="title" />

  <div :class="b()">
    <div :class="b('grid')">
      <div
        v-for="role in roles"
        :key="role.id"
        :class="b('card', { dirty: !!dirtyRoles[role.id] })"
      >
        <!-- Card Header: role name + save button -->
        <div :class="b('card-header')">
          <span :class="b('role-name')">{{ role.name }}</span>
          <a-button
            type="primary"
            size="small"
            :disabled="!dirtyRoles[role.id]"
            :loading="!!savingRoles[role.id]"
            :class="b('save-btn', { active: !!dirtyRoles[role.id] })"
            @click="saveRole(role.id)"
          >
            <template #icon>
              <SaveOutlined />
            </template>
            Save
          </a-button>
        </div>

        <!-- Collapsible permission panels -->
        <a-collapse :bordered="false" :class="b('collapse')">
          <!-- Page Permissions panel -->
          <a-collapse-panel key="page">
            <template #header>
              <span :class="b('panel-title')">
                Page Permissions
                <span :class="b('panel-count')">
                  ({{ enabledCount(role.id, 'page') }}/{{ pagePermissions.length }})
                </span>
              </span>
            </template>

            <div :class="b('perm-list')">
              <div
                v-for="perm in pagePermissions"
                :key="perm.key"
                :class="b('perm-item')"
              >
                <a-switch
                  size="small"
                  :checked="isEnabled(role.id, perm.key, 'page')"
                  @change="togglePermission(role.id, perm.key, 'page')"
                />
                <span :class="b('perm-label')">{{ perm.label }}</span>
              </div>
            </div>
          </a-collapse-panel>

          <!-- Action Permissions panel -->
          <a-collapse-panel key="action">
            <template #header>
              <span :class="b('panel-title')">
                Action Permissions
                <span :class="b('panel-count')">
                  ({{ enabledCount(role.id, 'action') }}/{{ actionPermissions.length }})
                </span>
              </span>
            </template>

            <div :class="b('perm-list')">
              <div
                v-for="perm in actionPermissions"
                :key="perm.key"
                :class="b('perm-item')"
              >
                <a-switch
                  size="small"
                  :checked="isEnabled(role.id, perm.key, 'action')"
                  @change="togglePermission(role.id, perm.key, 'action')"
                />
                <span :class="b('perm-label')">{{ perm.label }}</span>
              </div>
            </div>
          </a-collapse-panel>
        </a-collapse>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.permissions-page {
  padding: 0 24px 24px;

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 20px;
  }

  &__card {
    background: var(--Background-Main);
    border: 1px solid var(--Border-Light);
    border-radius: 8px;
    box-shadow: var(--Shadow-Small);
    overflow: hidden;
    transition: border-color 0.2s ease;

    &_dirty {
      border-color: var(--Button-Primary);
    }
  }

  &__card-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid var(--Border-Light);
  }

  &__role-name {
    font-size: 16px;
    font-weight: 600;
    color: var(--Text-Primary);
  }

  &__save-btn {
    opacity: 0.5;
    transition: opacity 0.2s ease;

    &_active {
      opacity: 1;
    }
  }

  &__collapse {
    background: transparent;

    :deep(.ant-collapse-item) {
      border-bottom: 1px solid var(--Border-Light);

      &:last-child {
        border-bottom: none;
      }
    }

    :deep(.ant-collapse-header) {
      padding: 12px 20px !important;
      color: var(--Text-Primary) !important;
    }

    :deep(.ant-collapse-content-box) {
      padding: 0 20px 12px !important;
    }
  }

  &__panel-title {
    font-size: 14px;
    font-weight: 500;
    color: var(--Text-Primary);
  }

  &__panel-count {
    font-weight: 400;
    color: var(--Text-Secondary);
    margin-left: 4px;
  }

  &__perm-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__perm-item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 4px 0;
  }

  &__perm-label {
    font-size: 13px;
    color: var(--Text-Primary);
    user-select: none;
  }
}
</style>
